import { useFetchWidgetID } from "@faire/retailer-visitor-shared/hooks/hCaptcha/useFetchWidgetId";
import {
  hCaptchaQueryKey,
  logHCaptchaError,
} from "@faire/retailer-visitor-shared/lib/captchaUtils";
import {
  getSettingExperimentalHCaptchaTimeoutMS,
  getSettingAndAssignEnableExperimentalLoginLatencyInterventions,
  getSettingHCaptchaTimeoutMS,
} from "@faire/retailer-visitor-shared/settings/getSettingHCaptchaTimeoutMS";
import { getSettingHCaptchaTokenStaleTimeS } from "@faire/retailer-visitor-shared/settings/getSettingHCaptchaTokenStaleTimeS";
import { useHCaptchaStore } from "@faire/retailer-visitor-shared/stores/domain/HCaptchaStore";
import { delay } from "@faire/web--source/common/delay";
import { getGlobalProperty } from "@faire/web--source/common/globals/getGlobalProperty";
import { logError } from "@faire/web--source/common/logging";
import { TimingMarker } from "@faire/web--source/common/timings/TimingMarker";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { useMutation as usePostHCaptchaMutation } from "@faire/web-api--source/endpoints/www/api/v2/users/retailer/hcaptcha/post-hooks";
import { trackSigninHcaptchaApiLatencyTimingPair } from "@faire/web-api--source/events/signin/actionCategoryOther/hcaptchaApiLatencyTimingPair";
import {
  trackSigninHcaptchaUserWaitTiming,
  trackSigninHcaptchaUserWaitTimingTraceDataRequested,
  trackSigninHcaptchaUserWaitTimingTraceDataRetrieved,
  trackSigninHcaptchaUserWaitTimingTraceRetreiveFailed,
} from "@faire/web-api--source/events/signin/timing/hcaptchaUserWait";
import { ILoginRequest } from "@faire/web-api--source/indigofair/data/ILoginRequest";
import { ISubmitHCaptchaDataRequest } from "@faire/web-api--source/indigofair/data/ISubmitHCaptchaDataRequest";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

const S_TO_MS = 1000;
const timeBeforeRefetchMS = () => getSettingHCaptchaTokenStaleTimeS() * S_TO_MS;

/**
 * Prefetches hCaptcha token and returns fetchHCaptchaToken. Should be used ahead of calling
 * endpoints we want to protect with rate limiting. Races against timeout (value of
 * getSettingHCaptchaTimeoutMS()) to prevent degrading user experience too much. If timeout wins,
 * return an error.
 *
 * returns fetchHCaptchaToken(shouldPostTokenToBackend: boolean)
 * When called, returns prefetched value or kicks off a new fetch. If shouldPostTokenToBackend
 * is true, will also post the token to the backend – this might be desireable if we are not
 * including the token in another backend API request (e.g. the login request accepts an
 * hCaptcha token)
 */
export const useFetchHCaptchaToken = () => {
  const { postHCaptchaToBE } = usePostHCaptchaToBE();
  const { fetchTokenWithTimeout, prefetchToken } = useFetchTokenWithTimeout();

  prefetchToken();

  const fetchHCaptchaToken = useCallback(
    async (
      shouldPostTokenToBackend: boolean
    ): Promise<ILoginRequest.IHCaptchaData> => {
      trackPreFetchingToken();

      const data = await fetchTokenWithTimeout();

      trackPostFetchingToken(data);

      // we must await sending data to BE to avoid race conditions
      // with the requests made after this point (i.e. login)
      await postHCaptchaToBE(shouldPostTokenToBackend, data);

      return data;
    },
    [fetchTokenWithTimeout, postHCaptchaToBE]
  );

  return { fetchHCaptchaToken };
};

const trackPreFetchingToken = () => {
  trackSigninHcaptchaUserWaitTiming();
  trackSigninHcaptchaUserWaitTimingTraceDataRequested();
};

const trackPostFetchingToken = (data: ILoginRequest.IHCaptchaData) => {
  if (data.token) {
    trackSigninHcaptchaUserWaitTimingTraceDataRetrieved();
  } else {
    trackSigninHcaptchaUserWaitTimingTraceRetreiveFailed();
  }
};

const hCaptchaTimeout = async <T>(): Promise<T> => {
  const delayMS =
    getSettingAndAssignEnableExperimentalLoginLatencyInterventions()
      ? getSettingExperimentalHCaptchaTimeoutMS()
      : getSettingHCaptchaTimeoutMS();
  await delay(delayMS);
  throw new Error(ILoginRequest.IHCaptchaData.HCaptchaError.TIMED_OUT);
};

const useFetchTokenWithTimeout = () => {
  const { fetchWidgetID, widgetID } = useFetchWidgetID();
  const queryClient = useQueryClient();
  const { fetchHCaptchaData } = useFetchHCaptchaData();

  const prefetchToken = useCallback(() => {
    if (widgetID) {
      queryClient.prefetchQuery({
        queryKey: hCaptchaQueryKey(widgetID),
        queryFn: () => fetchHCaptchaData(widgetID),
        staleTime: timeBeforeRefetchMS(),
      });
    }
  }, [queryClient, widgetID, fetchHCaptchaData]);

  const fetchTokenWithTimeout =
    useCallback(async (): Promise<ILoginRequest.IHCaptchaData> => {
      try {
        const widgetID = await Promise.race([
          fetchWidgetID(),
          hCaptchaTimeout<string>(),
        ]);

        const promise = queryClient.fetchQuery({
          queryKey: hCaptchaQueryKey(widgetID),
          queryFn: () => fetchHCaptchaData(widgetID),
          staleTime: timeBeforeRefetchMS(),
        });

        const token = await Promise.race([
          promise,
          hCaptchaTimeout<ILoginRequest.IHCaptchaData>(),
        ]);

        return token;
      } catch (error: unknown) {
        return handleHCaptchaDataError(error);
      }
    }, [fetchWidgetID, queryClient, fetchHCaptchaData]);

  return { fetchTokenWithTimeout, prefetchToken };
};

const useFetchHCaptchaData = () => {
  const { widgetError } = useFetchWidgetID();

  const fetchHCaptchaData = useCallback(
    async (
      widgetID: string | undefined
    ): Promise<ILoginRequest.IHCaptchaData> => {
      if (widgetError) {
        return Promise.reject(new Error(widgetError));
      }
      if (!widgetID) {
        return Promise.reject(
          new Error(ILoginRequest.IHCaptchaData.HCaptchaError.NO_WIDGET)
        );
      }

      const hcaptcha = getGlobalProperty<HCaptcha | undefined>("hcaptcha");
      if (!hcaptcha?.execute) {
        return Promise.reject(
          new Error(ILoginRequest.IHCaptchaData.HCaptchaError.NO_SCRIPT)
        );
      }

      const fetchedAt = Date.now();

      const start = new TimingMarker();
      const res = await hcaptcha.execute(widgetID, { async: true });
      const end = new TimingMarker();

      trackSigninHcaptchaApiLatencyTimingPair({
        start,
        end,
      });

      if (!res?.response) {
        return Promise.reject(
          new Error(ILoginRequest.IHCaptchaData.HCaptchaError.NO_TOKEN)
        );
      }

      return ILoginRequest.IHCaptchaData.build({
        token: res.response,
        token_fetched_at: fetchedAt,
      });
    },
    [widgetError]
  );

  return { fetchHCaptchaData };
};

const usePostHCaptchaToBE = () => {
  const [{ submittedTokens }, setHCaptchaStore] = useHCaptchaStore([
    "submittedTokens",
  ]);
  const { mutateAsync: postHCaptcha } = usePostHCaptchaMutation();

  const postHCaptchaToBE = useCallback(
    async (
      shouldPostTokenToBackend: boolean,
      data: ILoginRequest.IHCaptchaData
    ) => {
      if (
        shouldPostTokenToBackend &&
        !submittedTokens.includes(data.token ?? "")
      ) {
        try {
          await postHCaptcha([
            ISubmitHCaptchaDataRequest.build({ h_captcha_data: data }),
          ]);
          setHCaptchaStore((stale) => ({
            ...stale,
            submittedTokens: [...stale.submittedTokens, data.token].filter(
              isNotUndefined
            ),
          }));
        } catch (error) {
          logError(error);
        }
      }
    },
    [postHCaptcha, setHCaptchaStore, submittedTokens]
  );

  return { postHCaptchaToBE };
};

const handleHCaptchaDataError = (
  error: unknown
): ILoginRequest.IHCaptchaData => {
  const errorToLog: ILoginRequest.IHCaptchaData.HCaptchaError =
    typeof error === "object"
      ? ((error as Error).message as ILoginRequest.IHCaptchaData.HCaptchaError)
      : (error as ILoginRequest.IHCaptchaData.HCaptchaError);
  if (
    !Object.values(ILoginRequest.IHCaptchaData.HCaptchaError).includes(
      errorToLog
    )
  ) {
    logHCaptchaError(
      errorToLog,
      ILoginRequest.IHCaptchaData.HCaptchaError.H_CAPTCHA_ERROR_UNKNOWN
    );
    return ILoginRequest.IHCaptchaData.build({
      error: ILoginRequest.IHCaptchaData.HCaptchaError.H_CAPTCHA_ERROR_UNKNOWN,
    });
  }
  logHCaptchaError(error, errorToLog);

  return ILoginRequest.IHCaptchaData.build({
    error: errorToLog,
  });
};
